import { Cross1Icon, IconSize } from '@protoss/ui';
import * as React from 'react';

export interface ContextDrawerProps {
  /** Whether the drawer is open */
  open: boolean;
  /** Called when the drawer should close (close button or Escape key) */
  onClose: () => void;
  /** Title text shown in the drawer header */
  title: string;
  /**
   * Optional subtitle shown below the title - useful for bulk-edit mode context,
   * e.g. "3 users selected".
   */
  subtitle?: string;
  /** Drawer body content */
  children: React.ReactNode;
  /**
   * Drawer width in pixels.
   * Default: 400. Respects the design system scale (not freeform).
   */
  width?: number;
  /**
   * When true, focus is trapped inside the drawer while it is open.
   * Defaults to false - the drawer is non-modal, so focus trap is opt-in.
   */
  trapFocus?: boolean;
  /**
   * When true, renders the drawer as an inline flex sibling rather than a
   * fixed overlay. Use when the drawer should push the adjacent content left
   * (e.g. inside MainContentShell via MainContentSidePanelContext) instead
   * of overlaying it. Requires the parent to be a flex row container.
   *
   * Default: false (fixed overlay, original behaviour).
   */
  inline?: boolean;
}

/**
 * ContextDrawer - shared right-side slide-over panel for admin and non-project pages.
 *
 * Architecture note: This is the correct pattern for admin/non-project pages that need a
 * context-sensitive detail/edit pane without the project-shell RightRail + LayoutContext
 * coupling. The project-page right-rail system (AppShell → RightRail → RightUtilityPane)
 * remains unchanged and is not used by this component.
 *
 * Positioning: `position: fixed`, offset from top by `--pane-header-height` so it sits
 * below the app shell header. This works correctly on any Protoss page that uses AppShell.
 *
 * Usage:
 * ```tsx
 * <ContextDrawer
 *   open={drawerOpen}
 *   onClose={() => setDrawerOpen(false)}
 *   title="Edit User"
 *   subtitle={isBulk ? `${count} users selected` : undefined}
 * >
 *   <MyDrawerContent />
 * </ContextDrawer>
 * ```
 *
 * See context/20-frontend/right-rail.md §ContextDrawer for full documentation.
 */
export function ContextDrawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  width = 400,
  inline = false,
}: ContextDrawerProps) {
  const asideRef = React.useRef<HTMLElement>(null);

  // Close on Escape key
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Use `inert` to hide from AT and block focus when closed.
  // `aria-hidden` alone causes a browser warning when a focused descendant
  // is hidden - `inert` prevents that by also blocking focus.
  React.useEffect(() => {
    const el = asideRef.current;
    if (!el) return;
    if (open) {
      el.removeAttribute('inert');
    } else {
      el.setAttribute('inert', '');
    }
  }, [open]);

  return (
    <aside
      ref={asideRef}
      className={inline ? 'context-drawer context-drawer--inline' : 'context-drawer'}
      data-open={open ? 'true' : 'false'}
      style={{ '--context-drawer-width': `${width}px` } as React.CSSProperties}
      aria-label={open ? title : undefined}
    >
      <div className="context-drawer-header">
        <div className="context-drawer-header-text">
          <span className="context-drawer-title">{title}</span>
          {subtitle && <span className="context-drawer-subtitle">{subtitle}</span>}
        </div>
        <button type="button" className="context-drawer-close" onClick={onClose} aria-label="Close">
          <Cross1Icon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
        </button>
      </div>
      <div className="context-drawer-body">{children}</div>
    </aside>
  );
}
